import {Component, OnInit, TemplateRef, Type, ViewChild} from '@angular/core';
import {MsalBroadcastService, MsalService} from '@azure/msal-angular';
import {AuthenticationResult, EventMessage, EventType, InteractionStatus} from '@azure/msal-browser';
import {catchError, filter, map, concatMap} from 'rxjs/operators';
import {UserService} from '../services/user.service';
import {UserExtendedDTO} from '../models/user/user-b2c-flow-dto.model';
import {UserCompaniesResponseDTO, UserCompany} from '../models/user/user-companies-response-dto.model';
import {Router} from '@angular/router';
import {CommonModule} from '@angular/common';
import {MatDividerModule} from '@angular/material/divider';
import {of} from 'rxjs/internal/observable/of';
import {NgxPermissionsModule, NgxPermissionsService} from 'ngx-permissions';
import {Modules} from '../enums/modules.enum';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatButtonModule} from '@angular/material/button';
import {MatDialog, MatDialogModule} from '@angular/material/dialog';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {CompaniesService} from '../services/companies.service';
import {ModulesService} from '../services/modules.service';
import {PermissionsService as BcPermissionsService} from '../services/permissions.service';
import {forkJoin, EMPTY} from 'rxjs';
import {switchMap, catchError as catchErrorOp} from 'rxjs/operators';
import {MatSelectModule} from '@angular/material/select';
import {FormsModule} from '@angular/forms';
import {AuthComponent} from '../auth/auth.component';
import {MatIconModule} from '@angular/material/icon';
import {SortByPipe} from '../shared/pipes/sort-by.pipe';
import {TenantContextService} from '../services/tenant-context.service';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, MatDividerModule, MatProgressSpinnerModule, MatButtonModule, MatDialogModule, MatFormFieldModule, MatInputModule, NgxPermissionsModule, MatSelectModule, FormsModule, AuthComponent, MatIconModule, SortByPipe],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit {
  @ViewChild('checkOrgNumberDialog') checkOrgNumberDialog!: TemplateRef<MatDialog>;
  @ViewChild('confirmCompanyJoinDialog') confirmCompanyJoinDialog!: TemplateRef<MatDialog>;
  @ViewChild('newCompanyDialog') newCompanyDialog!: TemplateRef<MatDialog>;
  @ViewChild('companySelectDialog') companySelectDialog!: TemplateRef<MatDialog>;
  @ViewChild('moduleSelectDialog') moduleSelectDialog!: TemplateRef<MatDialog>;
  loginDisplay = false;
  companyId!: number;
  isRegistrationOpen: boolean = false;
  selectedModule: number;
  companies: UserCompany[];
  activeDashboard: Type<unknown> | null = null;

  constructor(
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private userService: UserService,
    private permissionsService: NgxPermissionsService,
    public dialog: MatDialog,
    private companiesService: CompaniesService,
    private router: Router,
    private modulesService: ModulesService,
    private bcPermissionsService: BcPermissionsService,
    private tenantContext: TenantContextService,
  ) {}

  ngOnInit(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
        concatMap((result: EventMessage) => {
          const payload = result.payload as AuthenticationResult;
          this.authService.instance.setActiveAccount(payload.account);
          return this.checkNewUser(this.authService.instance.getActiveAccount()?.idTokenClaims);
        })
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.getUserOrganization();
        // this.setOnboardingPermissions();
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        if (this.loginDisplay) {
          this.getUserOrganization();
        } else {
          // this.authService.loginRedirect();
        }
      });

     const urlParams = new URLSearchParams(window.location.search);
     const moduleFromUrl = +(urlParams.get('module') || 0);
     if (moduleFromUrl) {
       this.modulesService.hasModuleAccess(moduleFromUrl).then(hasAccess => {
         if (hasAccess) {
           this.selectedModule = moduleFromUrl;
           this.modulesService.selectModule(moduleFromUrl);
           this.loadDashboard(moduleFromUrl);
         } else {
           this.modulesService.getFirstAccessibleModule().then(fallbackModule => {
             this.selectedModule = fallbackModule || 1;
             this.modulesService.selectModule(this.selectedModule);
             this.loadDashboard(this.selectedModule);
           });
         }
       });
     } else {
       this.selectedModule = this.modulesService.getSelectedModule();
       this.loadDashboard(this.selectedModule);
     }
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  /**
   * Lazily loads only the dashboard for the active module so the landing page
   * doesn't pull every module's bundle (corporate, both invoicing modules,
   * risk assessment + their heavy deps) into the initial download.
   */
  async loadDashboard(module: number): Promise<void> {
    switch (module) {
      case 1:
        this.activeDashboard = (await import('../corporate/corporate-home-table/corporate-home-table.component'))
          .CorporateHomeTableComponent;
        break;
      case 2:
        this.activeDashboard = (await import('../invoicing-module/components/dashboard/invoicing-dashboard.component'))
          .InvoicingDashboardComponent;
        break;
      case 3:
        this.activeDashboard = (await import('../risk-assement/risk-assessment.component'))
          .RiskAssessmentComponent;
        break;
      case 4:
        this.activeDashboard = (await import('../pluton-invoicing-module/components/dashboard/invoicing-dashboard.component'))
          .PlutonInvoicingDashboardComponent;
        break;
      default:
        this.activeDashboard = null;
    }
  }

  checkNewUser(claims: any) {
    if (claims) {
      const user: UserExtendedDTO = {
        azureObjectId: claims.oid,
        email: claims.emails[0],
        firstName: claims.given_name,
        lastName: claims.family_name
      };

      return this.userService.createUserfromADb2c(user).pipe(
        catchError(error => {
          throw error; // rethrow the error
        })
      );
    } else {
      return of(null);
    }
  }

  getUserOrganization() {
    // Tenant gate: when the subdomain resolved to an organization that is suspended/archived,
    // block the session entirely rather than loading data from its database.
    if (this.tenantContext.isResolved && !this.tenantContext.isActive) {
      this.denyTenantAccess('This organization is not active. Please contact your administrator.');
      return;
    }

    this.userService.getUserCompanies().pipe(
      // The companies call runs against the tenant's Identity database. A failure here for a
      // resolved tenant means the signed-in user does not belong to this organization.
      catchError(err => {
        if (this.tenantContext.isResolved) {
          this.denyTenantAccess('Your account does not have access to this organization.');
          return EMPTY;
        }
        throw err;
      }),
      map((response: UserCompaniesResponseDTO) => {
        if (response.organizationId) {
          this.isRegistrationOpen = false;
          localStorage.setItem('organization', JSON.stringify(response.organizationId));
          const companyId = +localStorage.getItem('managedCompanyId');
          if (companyId) {
            this.setOnboardingPermissions()
            return;
          }
          this.companies = response.companies;
          this.companiesService.setManagedCompanyId(response.companies[0]?.id);
          this.setOnboardingPermissions();
          this.dialog.open(this.companySelectDialog);
          return
        } else {
          // this.setOnboardingPermissions();
          this.dialog.open(this.checkOrgNumberDialog)
        }
      })
    ).subscribe();
  }

  private denyTenantAccess(message: string) {
    this.loginDisplay = false;
    this.tenantContext.clear();
    localStorage.removeItem('organization');
    // eslint-disable-next-line no-alert
    alert(message);
    this.authService.logoutRedirect({postLogoutRedirectUri: '/'});
  }

  setOnboardingPermissions() {
    const companyId = this.companiesService.managedCompanyId;
    forkJoin({
      onboarding: this.userService.getModulePermissions(Modules.Onboarding),
      corporate: this.userService.getModulePermissionsById(companyId, Modules.Corporate),
      invoicing: this.userService.getModulePermissionsById(companyId, Modules.Invoicing),
      risk: this.userService.getModulePermissionsById(companyId, Modules.Risk),
    }).subscribe(({corporate, onboarding, invoicing, risk}) => {
      this.permissionsService.loadPermissions(onboarding.concat(corporate, invoicing, risk))
      localStorage.setItem('permissions', JSON.stringify(onboarding));
      localStorage.setItem('corporatePermissions', JSON.stringify(corporate));
      localStorage.setItem('invoicingPermissions', JSON.stringify(invoicing));
      localStorage.setItem('riskPermissions', JSON.stringify(risk));

      // Role-based effective permissions for the active company. Fire-and-forget — the
      // role system runs alongside the legacy module flags during the cutover (Phase 7),
      // so a failure here shouldn't block the user from landing on the home page. The
      // page-level *hasPermission directive falls back to "deny" until the cache fills.
      this.userService.getCurrentUser(false).pipe(
        switchMap(user => this.bcPermissionsService.refreshEffectivePermissions(user.userId, companyId)),
        catchErrorOp(() => EMPTY)
      ).subscribe();
    })
  }

  checkOrganizationNumber(orgNumber: string) {
    this.companiesService.checkRegistrationNumber(orgNumber)
      .subscribe((number)=> {
        if (!number) {
          this.dialog.closeAll();
          this.dialog.open(this.newCompanyDialog);
        } else {
          this.companyId = number;
          this.dialog.closeAll();
          this.dialog.open(this.confirmCompanyJoinDialog);
        }
      })
  }

  onReject() {
    this.dialog.closeAll()
    this.dialog.open(this.checkOrgNumberDialog)
  }

  onConfirmJoin() {
    this.companiesService.requestJoinCompany(this.companyId).subscribe();
  }

  onCompanyCreate() {
    this.router.navigate(['/onboarding/company'], {
      queryParams: {access: true}
    });
    this.dialog.closeAll();
  }

  goToModules() {
    this.dialog.closeAll();
    this.dialog.open(this.moduleSelectDialog);
    this.setOnboardingPermissions();
  }

  onCompanySelect(e: number) {
    this.companiesService.setManagedCompanyId(e);
  }

  selectModule() {
    this.dialog.closeAll();
    this.modulesService.selectModule(this.selectedModule);
    this.router.navigate([''], { queryParams: { module: this.selectedModule } }).then(() => {
      window.location.reload();
    });
  }

  onModuleSelect($event: number) {
    this.dialog.closeAll();
    this.selectedModule = $event;
    this.modulesService.selectModule(this.selectedModule);
    if (this.selectedModule === 3) {
      setTimeout(() => {
        this.router.navigate([''], { queryParams: { module: this.selectedModule } }).then(() => {
          window.location.reload();
        });
      }, 1000)
    } else {
      this.router.navigate([''], { queryParams: { module: this.selectedModule } }).then(() => {
        window.location.reload();
      });
    }
  }
}
